import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import bonusService from "../../services/bonusService";

const DEFAULTS = {
  IT_MED: { 1: 0, 2: 0, 3: 450, 4: 600, 5: 600, 6: 700, 7: 700, 8: 800 },
  KV_FKD_DHF: { 1: 400, 2: 450, 3: 450, 4: 600, 5: 600, 6: 750 },
};

const PROFESSION_LABELS = {
  INFORMATIK: "Informatik",
  MEDIAMATIK: "Mediamatik",
  KV: "KV",
  DETAILHANDEL: "Detailhandel",
  KUNDENDIALOG: "Kundendialog",
};

// Mirrors the backend half-year code: months 1–7 → FSyy, otherwise HSyy.
// Each semester step is roughly six months, so offset shifts the reference date.
function semesterCodeForOffset(offset) {
  const d = new Date();
  d.setMonth(d.getMonth() + 6 * offset);
  const yy = String(d.getFullYear() % 100).padStart(2, "0");
  const month = d.getMonth() + 1;
  return month >= 1 && month <= 7 ? `FS${yy}` : `HS${yy}`;
}

const STATUS_LABELS = {
  COMPLETE: "Complete",
  OVERRIDDEN: "Overridden",
  MISSING_DATA: "Open data",
  NOT_CALCULATED: "Not calculated",
};

function StatusPill({ status }) {
  const config = {
    COMPLETE: "bg-green-50 text-green-700 border-green-200",
    OVERRIDDEN: "bg-amber-50 text-amber-700 border-amber-200",
    MISSING_DATA: "bg-gray-50 text-gray-500 border-gray-200",
    NOT_CALCULATED: "bg-gray-50 text-gray-500 border-gray-200",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
        config[status] || config.NOT_CALCULATED
      }`}
    >
      {STATUS_LABELS[status] || status || "Unknown"}
    </span>
  );
}

function MetricCard({ icon, label, value, hint, tone = "default" }) {
  const toneClass = tone === "brand" ? "text-sunrise-red" : "text-grey-dusk";

  return (
    <div className="card-brand p-4 bg-white">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <div className={`text-xl font-bold truncate ${toneClass}`}>
            {value}
          </div>
          {hint && <p className="text-xs text-gray-500 mt-1 truncate">{hint}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
          <i className={icon}></i>
        </div>
      </div>
    </div>
  );
}

export default function BonusLandingPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [scope, setScope] = useState(null);
  const [professionType, setProfessionType] = useState("");
  const [students, setStudents] = useState([]);
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState({});

  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalGroup, setModalGroup] = useState("IT_MED");

  // 0 = each learner's current semester, -1 = previous, etc.
  const [semesterOffset, setSemesterOffset] = useState(0);

  const loadStudents = useCallback(async (activeGroup, offset = 0) => {
    setListLoading(true);
    setError(null);

    try {
      const list = await bonusService.getVisibleBonusStudents(
        activeGroup || undefined,
        offset,
      );
      setStudents(list || []);
    } catch (e) {
      setError("Failed to load student list.");
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [myScope, cfg] = await Promise.all([
        bonusService.getMyBonusScope(),
        bonusService.getBonusConfig(),
      ]);

      setScope(myScope);
      setConfig(cfg);

      const initialEdit = {};
      (cfg.groups || []).forEach((g) => {
        initialEdit[g.groupKey] = {};
        (g.amounts || []).forEach((a) => {
          initialEdit[g.groupKey][a.semesterNumber] = a.amountChf;
        });
      });
      setEditing(initialEdit);

      const typeFromUrl = params.get("professionType");
      const legacyGroupFromUrl = params.get("group");
      const fallbackFromLegacy =
        legacyGroupFromUrl === "IT_MED"
          ? "INFORMATIK"
          : legacyGroupFromUrl === "KV_FKD_DHF"
            ? "KV"
            : null;

      const initialType =
        typeFromUrl && (typeFromUrl === "ALL" || myScope.allowedProfessionTypes?.includes(typeFromUrl))
          ? typeFromUrl
          : myScope.defaultProfessionType ||
            fallbackFromLegacy ||
            myScope.allowedProfessionTypes?.[0] ||
            (myScope.canSwitchProfessionType ? "ALL" : "");

      setProfessionType(initialType);

      if (initialType) {
        setModalGroup(
          ["INFORMATIK", "MEDIAMATIK"].includes(initialType)
            ? "IT_MED"
            : "KV_FKD_DHF",
        );
      }
    } catch (e) {
      setError("Failed to load bonus data.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (!professionType) return;

    loadStudents(professionType === "ALL" ? "ALL" : professionType, semesterOffset);

    const next = new URLSearchParams(params);
    next.set("professionType", professionType);
    next.delete("group");
    setParams(next, { replace: true });
  }, [professionType, semesterOffset, loadStudents, params, setParams]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return students.filter((s) => {
      if (!q) return true;

      const fullName = (s.fullName || "").trim().toLowerCase();
      const parts = fullName.split(/\s+/);
      const firstName = (s.firstName || parts[0] || "").toLowerCase();
      const lastName = (s.lastName || parts.slice(1).join(" ") || "").toLowerCase();

      return firstName.startsWith(q) || lastName.startsWith(q);
    });
  }, [students, search]);

  const typeLabel = useMemo(
    () => professionType === "ALL" ? "All professions" : (PROFESSION_LABELS[professionType] || professionType),
    [professionType],
  );

  const canFilterProfession =
    scope?.canSwitchProfessionType ||
    (scope?.allowedProfessionTypes?.length || 0) > 1;

  const currentSemesterTotal = useMemo(
    () =>
      visibleRows.reduce(
        (sum, row) => sum + (Number(row.currentSemesterBonusChf) || 0),
        0,
      ),
    [visibleRows],
  );

  const completeCount = useMemo(
    () =>
      visibleRows.filter(
        (row) =>
          row.currentSemesterStatus === "COMPLETE" ||
          row.currentSemesterStatus === "OVERRIDDEN",
      ).length,
    [visibleRows],
  );

  function openConfigModal() {
    setSuccess("");

    if (professionType) {
      setModalGroup(
        ["INFORMATIK", "MEDIAMATIK"].includes(professionType)
          ? "IT_MED"
          : "KV_FKD_DHF",
      );
    }

    setModalOpen(true);
  }

  function updateAmount(group, sem, value) {
    setEditing((prev) => ({
      ...prev,
      [group]: { ...prev[group], [sem]: value },
    }));
  }

  async function saveConfig() {
    setSaving(true);
    setError(null);

    try {
      const payload = {
        groups: (config.groups || []).map((g) => ({
          groupKey: g.groupKey,
          groupLabel: g.groupLabel,
          amounts: (g.amounts || []).map((a) => ({
            semesterNumber: a.semesterNumber,
            amountChf:
              Number(editing[g.groupKey]?.[a.semesterNumber] ?? a.amountChf) ||
              0,
          })),
        })),
      };

      const updated = await bonusService.updateBonusConfig(payload);
      setConfig(updated);
      setSuccess("Bonus amount settings saved.");
      await loadStudents(professionType, semesterOffset);
      setModalOpen(false);
    } catch (e) {
      setError("Failed to save bonus amount settings.");
    } finally {
      setSaving(false);
    }
  }

  function resetModalGroupToDefaults() {
    if (!DEFAULTS[modalGroup]) return;

    setEditing((prev) => {
      const next = { ...prev, [modalGroup]: { ...prev[modalGroup] } };

      Object.entries(DEFAULTS[modalGroup]).forEach(([sem, amount]) => {
        next[modalGroup][Number(sem)] = amount;
      });

      return next;
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-80 bg-gray-200 rounded mt-3 animate-pulse" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card-brand h-24 animate-pulse bg-white" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-coins text-sunrise-red mr-4"></i>
                  Bonus
                </h1>
                <p className="text-slate-cloud">
                  Review learner bonus values and configure fixed CHF amounts.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
                <div className="flex items-center gap-1.5 rounded-lg border border-pale-dawn bg-white px-1.5 py-1">
                  <button
                    onClick={() => setSemesterOffset((o) => Math.max(o - 1, -7))}
                    disabled={listLoading || semesterOffset <= -7}
                    className="w-8 h-8 rounded-md text-grey-dusk hover:bg-ice-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                    title="Previous semester"
                  >
                    <i className="fas fa-chevron-left text-xs"></i>
                  </button>
                  <span className="text-xs font-semibold text-grey-dusk text-center min-w-[6.5rem] select-none">
                    {semesterCodeForOffset(semesterOffset)}
                  </span>
                  <button
                    onClick={() => setSemesterOffset((o) => Math.min(o + 1, 0))}
                    disabled={listLoading || semesterOffset >= 0}
                    className="w-8 h-8 rounded-md text-grey-dusk hover:bg-ice-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                    title="Next semester"
                  >
                    <i className="fas fa-chevron-right text-xs"></i>
                  </button>
                </div>

                {canFilterProfession && (
                  <select
                    value={professionType}
                    onChange={(e) => setProfessionType(e.target.value)}
                    className="input-brand w-full sm:w-56"
                  >
                    <option value="ALL">All professions</option>
                    {(scope?.allowedProfessionTypes || []).map((p) => (
                      <option key={p} value={p}>
                        {PROFESSION_LABELS[p] || p}
                      </option>
                    ))}
                  </select>
                )}

                {scope?.canConfigureAmounts && (
                  <button
                    onClick={openConfigModal}
                    className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-sunrise-red text-white hover:bg-sunrise-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-sliders-h"></i>
                    CHF settings
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`mb-5 rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
              error
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-green-200 bg-green-50 text-green-700"
            }`}
          >
            <i
              className={`fas ${
                error ? "fa-exclamation-triangle" : "fa-check-circle"
              }`}
            ></i>
            {error || success}
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <MetricCard
            icon="fas fa-user-graduate"
            label="Visible learners"
            value={visibleRows.length}
            hint={`Profession: ${typeLabel || "All"}`}
          />
          <MetricCard
            icon="fas fa-check-circle"
            label="Ready semesters"
            value={`${completeCount}/${visibleRows.length}`}
            hint="Complete or overridden"
          />
          <MetricCard
            icon="fas fa-money-bill-wave"
            label="Current total"
            value={bonusService.formatCHF(currentSemesterTotal)}
            tone="brand"
          />
        </div>

        {/* Learners */}
        <div className="card-brand overflow-hidden">
          <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                  <i className="fas fa-list text-sunrise-red mr-3"></i>
                  Apprentices
                </h2>
                <p className="text-sm text-slate-cloud mt-1">
                  Open a learner to review calculation details.
                </p>
              </div>

              <div className="relative w-full lg:w-96">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search by first or last name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-brand w-full pl-9"
                />
              </div>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden lg:block">
            <table className="w-full text-sm">
              <thead className="bg-ice-white text-gray-600 border-b border-pale-dawn">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                    Current semester
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                    Bonus %
                  </th>
                  <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wide">
                    Bonus CHF
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-pale-dawn">
                {listLoading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-gray-500">
                      Loading students...
                    </td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-gray-500">
                      No students found.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((s) => (
                    <tr
                      key={s.studentId}
                      className="hover:bg-ice-white/70 cursor-pointer transition-colors"
                      onClick={() => navigate(`/bonus/students/${s.studentId}`)}
                    >
                      <td className="px-5 py-3 font-semibold text-grey-dusk">
                        {s.fullName}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {s.currentSemesterLabel || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={s.currentSemesterStatus === "OVERRIDDEN" ? "COMPLETE" : s.currentSemesterStatus} />
                      </td>
                      <td className="px-4 py-3 text-right text-grey-dusk">
                        {bonusService.formatPercent(s.currentSemesterPercent)}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-sunrise-red">
                        {bonusService.formatCHF(s.currentSemesterBonusChf)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="lg:hidden p-4 space-y-3">
            {listLoading ? (
              <div className="text-center py-8 text-gray-500">
                Loading students...
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No students found.
              </div>
            ) : (
              visibleRows.map((s) => (
                <button
                  key={s.studentId}
                  type="button"
                  onClick={() => navigate(`/bonus/students/${s.studentId}`)}
                  className="w-full text-left rounded-xl border border-pale-dawn bg-white p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-grey-dusk truncate">
                        {s.fullName}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {s.currentSemesterLabel || "—"}
                      </div>
                      <div className="mt-2">
                        <StatusPill status={s.currentSemesterStatus === "OVERRIDDEN" ? "COMPLETE" : s.currentSemesterStatus} />
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-xs text-gray-500">Bonus</div>
                      <div className="font-bold text-sunrise-red">
                        {bonusService.formatCHF(s.currentSemesterBonusChf)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {bonusService.formatPercent(s.currentSemesterPercent)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="px-5 py-3 border-t border-pale-dawn bg-ice-white/40 text-xs text-gray-500">
            Showing {visibleRows.length} of {students.length}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl card-brand overflow-hidden bg-white">
            <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                    <i className="fas fa-sliders-h text-sunrise-red mr-3"></i>
                    Bonus amount settings
                  </h2>
                  <p className="text-sm text-slate-cloud mt-1">
                    Edit semester fixed amounts per profession group.
                  </p>
                </div>

                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-grey-dusk"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {(config?.groups || []).map((g) => (
                  <button
                    key={g.groupKey}
                    onClick={() => setModalGroup(g.groupKey)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      modalGroup === g.groupKey
                        ? "border-sunrise-red text-white bg-sunrise-red"
                        : "border-pale-dawn text-grey-dusk bg-white hover:border-sunrise-red/40"
                    }`}
                  >
                    {g.groupLabel}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 max-h-[55vh] overflow-auto">
              <div className="rounded-xl border border-pale-dawn overflow-hidden">
                {(
                  config?.groups?.find((g) => g.groupKey === modalGroup)?.amounts || []
                ).map((a, idx, arr) => (
                  <div
                    key={a.semesterNumber}
                    className={`flex items-center gap-3 px-3 py-2 ${idx < arr.length - 1 ? "border-b border-pale-dawn" : ""} ${idx % 2 === 0 ? "bg-white" : "bg-ice-white/40"}`}
                  >
                    <span className="text-xs font-semibold text-grey-dusk w-24 shrink-0">
                      Semester {a.semesterNumber}
                    </span>
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                        CHF
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="50"
                        value={editing[modalGroup]?.[a.semesterNumber] ?? a.amountChf}
                        onChange={(e) => updateAmount(modalGroup, a.semesterNumber, e.target.value)}
                        className="w-full pl-10 pr-3 py-1.5 text-xs border border-pale-dawn rounded-lg focus:outline-none focus:ring-1 focus:ring-sunrise-red bg-white"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-pale-dawn bg-ice-white/40 flex flex-col sm:flex-row sm:justify-end gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg text-sm border border-pale-dawn text-gray-600 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={resetModalGroupToDefaults}
                className="px-4 py-2 rounded-lg text-sm border border-pale-dawn text-grey-dusk bg-white hover:bg-gray-50"
              >
                Reset to defaults
              </button>
              <button
                onClick={saveConfig}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-sunrise-red text-white hover:bg-sunrise-600 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}